/**
 * The customer-facing legal documents, in one place.
 *
 * Why this file exists: until 2026-09-06 the consent checkboxes on both
 * registration surfaces linked to `/legal/terms` and `/legal/privacy`, and
 * NEITHER route existed. React Router resolved both through the catch-all to
 * the not-found page. A customer ticked "I accept the terms and the privacy
 * policy", clicked either link, and landed on a 404 — for a full year, on the
 * one screen where the documents actually have to be reachable.
 *
 * The fix is not two hardcoded hrefs. Three things have to stay in step and
 * kept drifting apart:
 *   1. what the checkbox CLAIMS the user is accepting,
 *   2. where that document can be READ,
 *   3. what gets RECORDED as accepted, with which version.
 * A single table drives all three: the label key, the href and the version
 * that lands in `company_audit_logs`.
 *
 * `href: null` is deliberate and load-bearing. As of 2026-09-06 only the
 * privacy policy and the imprint are published; there is no public URL for the
 * SaaS terms, the DPA or the TOMs (`veltric.app/agb`, `/avv` and `/toms` all
 * answer 404, measured the same day). Rather than invent a link that 404s
 * again, an unpublished document renders through `/legal/:doc` as a page that
 * names the document and says how to obtain it. `legalDocuments.test.ts` pins
 * that every document a user must CONSENT to is either published or explicitly
 * marked `pendingPublication` — so nobody can quietly add a required consent
 * pointing nowhere.
 *
 * Versions are the string recorded as proof. They are dates, not semver: the
 * question a data protection officer asks is "which text did this person see
 * on that day", and a date answers it against the document archive.
 */

export type LegalDocumentKey = "terms" | "privacy" | "dpa" | "toms" | "imprint";

export interface LegalDocument {
  key: LegalDocumentKey;
  /** Key under the `legal` namespace in the locale files. */
  labelKey: string;
  /**
   * Public URL, or null while the document is not published. Never point this
   * at a path that does not resolve — that is the bug this file replaces.
   */
  href: string | null;
  /** Recorded verbatim as the accepted version. Bump when the text changes. */
  version: string;
  /**
   * True when the document is part of a consent the user gives at signup.
   * Consent documents are held to the publication rule in the test.
   */
  consent: boolean;
  /**
   * Acknowledges that a consent document has no public URL yet. Exists so the
   * gap is a visible, dated decision instead of a silent null.
   */
  pendingPublication?: boolean;
  /**
   * True when a consent box's LABEL links to the document — the words the
   * person ticks beside. Wider than `consent`: the DPA box links the TOMs
   * (annex C of the DPA) next to the DPA, though only the DPA is recorded as
   * accepted. `legalDocuments.test.ts` holds every linked document to the
   * publication rule (the S-0 box-ship gate; W9 audit lens-6 F12) and
   * `signupConsentBoxes.test.tsx` pins that the rendered labels link exactly
   * this set — so the gate is derived from the table, not hard-coded.
   */
  linkedByConsentLabel?: boolean;
}

export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [
  {
    key: "terms",
    labelKey: "terms",
    // Not published anywhere as of 2026-09-06 — `veltric.app/agb`,
    // `/nutzungsbedingungen` and `/terms` all answer 404, and the help centre
    // carries only the imprint and the privacy policy.
    href: null,
    version: "2026-09-06",
    consent: true,
    pendingPublication: true,
    linkedByConsentLabel: true,
  },
  {
    key: "privacy",
    labelKey: "privacy",
    href: "https://veltric.app/datenschutz",
    version: "2026-09-06",
    consent: true,
    linkedByConsentLabel: true,
  },
  {
    key: "dpa",
    labelKey: "dpa",
    // AVV incl. annexes A–C. `veltric.app/avv` answers 404 (2026-09-06).
    href: null,
    version: "2026-09-06",
    consent: true,
    pendingPublication: true,
    linkedByConsentLabel: true,
  },
  {
    key: "toms",
    labelKey: "toms",
    // Annex C of the DPA. `veltric.app/toms` answers 404 (2026-09-06).
    href: null,
    version: "2026-09-06",
    consent: false,
    pendingPublication: true,
    // Linked from the DPA box's label (DpaConsentCheckbox) although never
    // recorded as accepted — so it is part of the box-ship gate.
    linkedByConsentLabel: true,
  },
  {
    key: "imprint",
    labelKey: "imprint",
    href: "https://veltric.app/impressum",
    version: "2026-09-06",
    consent: false,
  },
] as const;

export function legalDocument(key: LegalDocumentKey): LegalDocument {
  const doc = LEGAL_DOCUMENTS.find((d) => d.key === key);
  // Callers pass a literal from the union, so a miss means the table lost an
  // entry — louder than rendering a checkbox with no document behind it.
  if (!doc) throw new Error(`Unknown legal document: ${key}`);
  return doc;
}

/**
 * The documents a consent box's LABEL links to (`linkedByConsentLabel`), in
 * table order — what the S-0 box-ship gate holds to the publication rule.
 */
export function consentLabelLinkedDocuments(): LegalDocument[] {
  return LEGAL_DOCUMENTS.filter((d) => d.linkedByConsentLabel === true);
}

/** In-app path for a document. Always resolves — see `LegalDocumentPage`. */
export function legalDocumentPath(key: LegalDocumentKey): string {
  return `/legal/${key}`;
}

/**
 * The `{key: version}` map sent with a registration and recorded as the proof
 * of consent. Order follows the table so the audit row reads the same way
 * every time.
 */
export function consentDocumentVersions(): Record<string, string> {
  return Object.fromEntries(
    LEGAL_DOCUMENTS.filter((d) => d.consent).map((d) => [d.key, d.version]),
  );
}

/**
 * How the documents were put in front of the person, sent beside the versions
 * as `presentation`. The backend keeps the value on the audit row, so the
 * record can say "ticked boxes" apart from the 2026-10 notice rows
 * (`LEGAL_TERMS_NOTICE_SHOWN`) and from API-direct callers (stamped `api`
 * server-side). Both registration forms ask for explicit ticks again (W9
 * 1-S): a terms+privacy box on every lane, the separate DPA box only where a
 * company is created — an invitee joins an organisation whose DPA already
 * stands.
 */
export const CHECKBOX_PRESENTATION = "checkbox";

/**
 * What a registration with ticked boxes sends as `acceptedDocuments`: the
 * consent documents' versions, optionally narrowed to the documents whose box
 * was actually on the screen (`["terms", "privacy"]` on the invite lane),
 * tagged as ticked. A key that is not a consent document is a programming
 * error, not a narrower map — it throws rather than silently sending less
 * proof than the caller believes.
 */
export function checkboxDocumentVersions(
  keys?: readonly LegalDocumentKey[],
): Record<string, string> {
  const all = consentDocumentVersions();
  if (keys) {
    for (const key of keys) {
      if (!(key in all)) throw new Error(`Not a consent document: ${key}`);
    }
  }
  const versions = keys
    ? Object.fromEntries(Object.entries(all).filter(([key]) => keys.includes(key as LegalDocumentKey)))
    : all;
  return { ...versions, presentation: CHECKBOX_PRESENTATION };
}

/** The public price overview the order notice and the order mail point to. */
export const PRICE_OVERVIEW_URL = "https://veltric.app/preise";
